import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Briefcase, Send, CheckCircle, Mail, Plus, Trash2, Phone, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { useHaptics } from "@/hooks/useHaptics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = process.env.REACT_APP_LOGO_URL;

const TASK_OPTIONS = [
  { id: "photography", label: "Inventory photography" },
  { id: "listing", label: "Draft creation / Listing write-up" },
  { id: "shipping", label: "Shipping and packing" },
  { id: "cleaning", label: "Items cleaning / prep" },
];

export default function JobApplicationForm() {
  const { heavyPress, lightTap, successFeedback, errorFeedback } = useHaptics();
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    address: "",
    resume_text: "",
    why_join: "",
    availability: "",
    tasks_able_to_perform: [],
    background_check_consent: null,
    has_reliable_transportation: null,
    additional_info: "",
    preferred_contact: "email", // "email" or "text"
    // Work history - last 2 years (start with 1, can add more)
    work_history: [
      {
        employer: "",
        employer_address: "",
        employer_phone: "",
        dates_from: "",
        dates_to: "",
        title: "",
        responsibilities: "",
        reason_for_leaving: "",
        may_contact: null
      }
    ]
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Silent visitor tracking (invisible to applicants)
  useEffect(() => {
    const trackVisit = async () => {
      try {
        // Generate a simple visitor ID based on browser fingerprint
        const visitorId = `${navigator.userAgent.slice(0, 50)}_${screen.width}x${screen.height}`;
        await axios.post(`${API}/forms/track-visit`, null, {
          params: {
            page: "job-application",
            visitor_id: visitorId,
            user_agent: navigator.userAgent
          }
        });
      } catch (err) {
        // Silently fail - don't affect user experience
      }
    };
    trackVisit();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTaskChange = (taskId, checked) => {
    if (checked) {
      setFormData({
        ...formData,
        tasks_able_to_perform: [...formData.tasks_able_to_perform, taskId]
      });
    } else {
      setFormData({
        ...formData,
        tasks_able_to_perform: formData.tasks_able_to_perform.filter(t => t !== taskId)
      });
    }
  };

  const handleWorkHistoryChange = (index, field, value) => {
    const updatedHistory = [...formData.work_history];
    updatedHistory[index] = { ...updatedHistory[index], [field]: value };
    setFormData({ ...formData, work_history: updatedHistory });
  };

  const addWorkHistoryEntry = () => {
    lightTap();
    setFormData({
      ...formData,
      work_history: [
        ...formData.work_history,
        {
          employer: "",
          employer_address: "",
          employer_phone: "",
          dates_from: "",
          dates_to: "",
          title: "",
          responsibilities: "",
          reason_for_leaving: "",
          may_contact: null
        }
      ]
    });
  };

  const removeWorkHistoryEntry = (index) => {
    lightTap();
    const updatedHistory = formData.work_history.filter((_, i) => i !== index);
    setFormData({ ...formData, work_history: updatedHistory });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    heavyPress(); // Haptic on form submit
    setLoading(true);

    try {
      console.log('Submitting job application to:', `${API}/forms/job-application`);
      const response = await axios.post(`${API}/forms/job-application`, formData);
      console.log('Submission successful:', response.data);
      setSubmitted(true);
      successFeedback();
      toast.success("Application submitted successfully!");
    } catch (error) {
      console.error('Submission error:', error);
      errorFeedback();
      const errorMessage = error.response?.data?.detail || error.message || "Failed to submit application";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A1A2E] via-[#16213E] to-[#0F3460] py-8 px-4" data-testid="job-application-success">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl p-8 text-center"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-poppins text-2xl font-bold text-[#1A1A2E] mb-2">
              Application Received!
            </h2>
            <p className="text-[#666] mb-4">
              Thank you for your interest in joining our team. We will review your application and get back to you soon.
            </p>
            <div className="mb-6 bg-gradient-to-r from-[#00D4FF]/20 to-[#8B5CF6]/20 border-2 border-[#00D4FF] rounded-xl p-4">
              <div className="flex items-center justify-center gap-3">
                <div className="w-10 h-10 bg-[#00D4FF] rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <p className="text-[#1A1A2E] font-semibold text-base">
                  We have sent a confirmation email to your inbox!
                </p>
              </div>
            </div>
            <Link to="/" onClick={() => lightTap()}>
              <Button className="bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] hover:from-[#00A8CC] hover:to-[#6D28D9] text-white font-semibold px-8 py-3 rounded-lg shadow-lg" data-testid="back-to-home-btn">
                Back to Home
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A1A2E] via-[#16213E] to-[#0F3460] py-8 px-4" data-testid="job-application-page">
      <div className="max-w-2xl mx-auto">
        {/* Back Link and Logo Row */}
        <div className="relative mt-8 mb-6">
          {/* Back Link - Aligned with logo */}
          <Link to="/" onClick={() => lightTap()} className="absolute left-0 top-0 inline-flex items-center gap-2 text-white/70 hover:text-[#00D4FF] transition-colors" data-testid="back-link-top">
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>

          {/* Logo - Centered */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-24 h-24 mx-auto rounded-xl overflow-hidden shadow-2xl ring-4 ring-white/20"
          >
            <img src={LOGO_URL} alt="Thrifty Curator Logo" className="w-full h-full object-cover" />
          </motion.div>
        </div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="font-poppins text-3xl font-bold text-white mb-2">Job Application</h1>
          <p className="text-white/60">Join our team</p>
        </motion.div>

        {/* Form Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-2xl overflow-hidden"
        >
          <div className="h-1.5 bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6]" />
          <form onSubmit={handleSubmit} className="p-6 space-y-5" data-testid="job-application-form">
            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Full Name *</Label>
              <Input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                placeholder="Enter your full name"
                className="border-2 border-gray-200 focus:border-[#00D4FF] rounded-lg"
                data-testid="input-full-name"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Email Address *</Label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="your@email.com"
                className="border-2 border-gray-200 focus:border-[#00D4FF] rounded-lg"
                data-testid="input-email"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Phone Number *</Label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="(555) 123-4567"
                className="border-2 border-gray-200 focus:border-[#00D4FF] rounded-lg"
                data-testid="input-phone"
              />
            </div>

            {/* Contact Info Note */}
            <div>
              <p className="text-xs text-gray-500">
                We'll send updates about your application to your email address.
              </p>
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Current Address *</Label>
              <Textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                placeholder="Street Address, City, State, ZIP Code"
                className="border-2 border-gray-200 focus:border-[#00D4FF] rounded-lg min-h-[80px]"
                data-testid="input-address"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Tell us about your experience *</Label>
              <Textarea
                name="resume_text"
                value={formData.resume_text}
                onChange={handleChange}
                required
                placeholder="Share your relevant work experience, skills, and any experience with reselling platforms..."
                className="border-2 border-gray-200 focus:border-[#00D4FF] rounded-lg min-h-[120px]"
                data-testid="input-resume"
              />
            </div>

            {/* Work History Section */}
            <div className="border-t-2 border-gray-100 pt-6">
              <div className="flex items-center gap-2 mb-4">
                <Briefcase className="w-5 h-5 text-[#8B5CF6]" />
                <Label className="text-base font-bold text-[#1A1A2E]">Work History (Last 2 Years)</Label>
              </div>
              <p className="text-sm text-gray-500 mb-4">Please list your most recent employment first.</p>
              
              {formData.work_history.map((job, index) => (
                <div key={index} className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200 relative">
                  <div className="flex justify-between items-start mb-4">
                    <p className="text-sm font-semibold text-[#8B5CF6]">
                      {index === 0 ? "Most Recent Employer" : `Previous Employer ${index}`}
                    </p>
                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => removeWorkHistoryEntry(index)}
                        className="text-red-500 hover:text-red-700 p-1 transition-colors"
                        data-testid={`work-history-${index}-remove`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-semibold text-[#1A1A2E] mb-1 block">Past Employer</Label>
                      <Input
                        type="text"
                        value={job.employer}
                        onChange={(e) => handleWorkHistoryChange(index, 'employer', e.target.value)}
                        placeholder="Company name"
                        className="border-2 border-gray-200 focus:border-[#00D4FF] rounded-lg bg-white"
                        data-testid={`work-history-${index}-employer`}
                      />
                    </div>

                    <div>
                      <Label className="text-sm font-semibold text-[#1A1A2E] mb-1 block">Company Address</Label>
                      <Input
                        type="text"
                        value={job.employer_address}
                        onChange={(e) => handleWorkHistoryChange(index, 'employer_address', e.target.value)}
                        placeholder="Street, City, State, ZIP"
                        className="border-2 border-gray-200 focus:border-[#00D4FF] rounded-lg bg-white"
                        data-testid={`work-history-${index}-address`}
                      />
                    </div>

                    <div>
                      <Label className="text-sm font-semibold text-[#1A1A2E] mb-1 block">Company Phone</Label>
                      <Input
                        type="tel"
                        value={job.employer_phone}
                        onChange={(e) => handleWorkHistoryChange(index, 'employer_phone', e.target.value)}
                        placeholder="(555) 123-4567"
                        className="border-2 border-gray-200 focus:border-[#00D4FF] rounded-lg bg-white"
                        data-testid={`work-history-${index}-phone`}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm font-semibold text-[#1A1A2E] mb-1 block">From</Label>
                        <Input
                          type="text"
                          value={job.dates_from}
                          onChange={(e) => handleWorkHistoryChange(index, 'dates_from', e.target.value)}
                          placeholder="MM/YYYY"
                          className="border-2 border-gray-200 focus:border-[#00D4FF] rounded-lg bg-white"
                          data-testid={`work-history-${index}-from`}
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-[#1A1A2E] mb-1 block">To</Label>
                        <Input
                          type="text"
                          value={job.dates_to}
                          onChange={(e) => handleWorkHistoryChange(index, 'dates_to', e.target.value)}
                          placeholder="MM/YYYY or Present"
                          className="border-2 border-gray-200 focus:border-[#00D4FF] rounded-lg bg-white"
                          data-testid={`work-history-${index}-to`}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-sm font-semibold text-[#1A1A2E] mb-1 block">Your Title</Label>
                      <Input
                        type="text"
                        value={job.title}
                        onChange={(e) => handleWorkHistoryChange(index, 'title', e.target.value)}
                        placeholder="Job title"
                        className="border-2 border-gray-200 focus:border-[#00D4FF] rounded-lg bg-white"
                        data-testid={`work-history-${index}-title`}
                      />
                    </div>
                    
                    <div>
                      <Label className="text-sm font-semibold text-[#1A1A2E] mb-1 block">What was your primary responsibility or responsibilities?</Label>
                      <Textarea
                        value={job.responsibilities}
                        onChange={(e) => handleWorkHistoryChange(index, 'responsibilities', e.target.value)}
                        placeholder="Describe your main duties and responsibilities..."
                        className="border-2 border-gray-200 focus:border-[#00D4FF] rounded-lg min-h-[80px] bg-white"
                        data-testid={`work-history-${index}-responsibilities`}
                      />
                    </div>
                    
                    <div>
                      <Label className="text-sm font-semibold text-[#1A1A2E] mb-1 block">Why did you leave?</Label>
                      <Textarea
                        value={job.reason_for_leaving}
                        onChange={(e) => handleWorkHistoryChange(index, 'reason_for_leaving', e.target.value)}
                        placeholder="Reason for leaving..."
                        className="border-2 border-gray-200 focus:border-[#00D4FF] rounded-lg min-h-[60px] bg-white"
                        data-testid={`work-history-${index}-reason`}
                      />
                    </div>
                    
                    <div>
                      <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">May we contact this employer?</Label>
                      <div className="flex gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`contact_employer_${index}`}
                            checked={job.may_contact === true}
                            onChange={() => handleWorkHistoryChange(index, 'may_contact', true)}
                            className="w-5 h-5 accent-[#00D4FF]"
                            data-testid={`work-history-${index}-contact-yes`}
                          />
                          <span className="text-sm text-[#1A1A2E]">Yes</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`contact_employer_${index}`}
                            checked={job.may_contact === false}
                            onChange={() => handleWorkHistoryChange(index, 'may_contact', false)}
                            className="w-5 h-5 accent-[#00D4FF]"
                            data-testid={`work-history-${index}-contact-no`}
                          />
                          <span className="text-sm text-[#1A1A2E]">No</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Employer Button */}
              <button
                type="button"
                onClick={addWorkHistoryEntry}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 border-2 border-dashed border-[#8B5CF6]/50 rounded-lg text-[#8B5CF6] hover:bg-[#8B5CF6]/10 hover:border-[#8B5CF6] transition-all duration-200"
                data-testid="add-work-history-btn"
              >
                <Plus className="w-5 h-5" />
                Add Another Employer
              </button>
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Why do you want to join our team? *</Label>
              <Textarea
                name="why_join"
                value={formData.why_join}
                onChange={handleChange}
                required
                placeholder="Tell us what excites you about this opportunity..."
                className="border-2 border-gray-200 focus:border-[#00D4FF] rounded-lg min-h-[100px]"
                data-testid="input-why-join"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Availability *</Label>
              <Input
                type="text"
                name="availability"
                value={formData.availability}
                onChange={handleChange}
                required
                placeholder="e.g., specify days of the week and time available"
                className="border-2 border-gray-200 focus:border-[#00D4FF] rounded-lg"
                data-testid="input-availability"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Which tasks are you able to perform? *</Label>
              <p className="text-sm text-gray-500 mb-3">Select all that apply</p>
              <div className="space-y-3">
                {TASK_OPTIONS.map((task) => (
                  <div key={task.id} className="flex items-center gap-3">
                    <Checkbox
                      id={task.id}
                      checked={formData.tasks_able_to_perform.includes(task.id)}
                      onCheckedChange={(checked) => handleTaskChange(task.id, checked)}
                      className="w-6 h-6 border-2 border-gray-300 data-[state=checked]:bg-[#00D4FF] data-[state=checked]:border-[#00D4FF]"
                      data-testid={`task-${task.id}`}
                    />
                    <Label htmlFor={task.id} className="text-sm text-[#1A1A2E] cursor-pointer font-normal">
                      {task.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Are you willing/able to submit to a background check? *</Label>
              <div className="flex gap-6 mt-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="background_check"
                    value="yes"
                    checked={formData.background_check_consent === true}
                    onChange={() => setFormData({ ...formData, background_check_consent: true })}
                    className="w-6 h-6 accent-[#00D4FF]"
                    data-testid="background-check-yes"
                  />
                  <span className="text-sm text-[#1A1A2E]">Yes</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="background_check"
                    value="no"
                    checked={formData.background_check_consent === false}
                    onChange={() => setFormData({ ...formData, background_check_consent: false })}
                    className="w-6 h-6 accent-[#00D4FF]"
                    data-testid="background-check-no"
                  />
                  <span className="text-sm text-[#1A1A2E]">No</span>
                </label>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Do you have reliable transportation? *</Label>
              <div className="flex gap-6 mt-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="transportation"
                    value="yes"
                    checked={formData.has_reliable_transportation === true}
                    onChange={() => setFormData({ ...formData, has_reliable_transportation: true })}
                    className="w-6 h-6 accent-[#00D4FF]"
                    data-testid="transportation-yes"
                  />
                  <span className="text-sm text-[#1A1A2E]">Yes</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="transportation"
                    value="no"
                    checked={formData.has_reliable_transportation === false}
                    onChange={() => setFormData({ ...formData, has_reliable_transportation: false })}
                    className="w-6 h-6 accent-[#00D4FF]"
                    data-testid="transportation-no"
                  />
                  <span className="text-sm text-[#1A1A2E]">No</span>
                </label>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-[#1A1A2E] mb-2 block">Is there any additional information you wish to include?</Label>
              <Textarea
                name="additional_info"
                value={formData.additional_info}
                onChange={handleChange}
                placeholder="Any other information you'd like to share..."
                className="border-2 border-gray-200 focus:border-[#00D4FF] rounded-lg min-h-[100px]"
                data-testid="input-additional-info"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] hover:from-[#00A8CC] hover:to-[#6D28D9] text-white font-semibold py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
              data-testid="submit-job-application-btn"
            >
              {loading ? (
                "Submitting..."
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Application
                </>
              )}
            </Button>
          </form>
        </motion.div>

        {/* Back to Home - Easy access at bottom */}
        <Link 
          to="/" 
          onClick={() => lightTap()}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 py-4 px-6 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors font-medium"
          data-testid="back-link"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </Link>
      </div>
    </div>
  );
}
